import React, { useState } from 'react';
import { SearchIcon, FilterIcon, CalendarIcon, UserIcon, HomeIcon, CheckCircleIcon, XCircleIcon, ClockIcon, EyeIcon } from 'lucide-react';
import { mockUserBookings, allProperties } from '../../utils/mockData';
const AdminBookingList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  // Create expanded booking data with property details
  const bookings = mockUserBookings.map(booking => {
    return {
      ...booking,
      propertyTitle: booking.property.title,
      propertyLocation: booking.property.location,
      propertyImage: booking.property.images[0],
      guestName: 'Guest ' + booking.id,
      bookingDate: '2023-09-' + (booking.id + 10) // Mock booking date
    };
  });
  // Filter bookings based on search term and status
  const filteredBookings = bookings.filter(booking => {
    const matchesSearch = booking.propertyTitle.toLowerCase().includes(searchTerm.toLowerCase()) || booking.propertyLocation.toLowerCase().includes(searchTerm.toLowerCase()) || booking.guestName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || booking.status === filterStatus;
    return matchesSearch && matchesStatus;
  });
  const getStatusBadge = status => {
    switch (status) {
      case 'confirmed':
        return <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-24">
            Confirmed
          </span>;
      case 'pending':
        return <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800" data-id="element-25">
            Pending
          </span>;
      case 'completed':
        return <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-26">
            Completed
          </span>;
      case 'cancelled':
        return <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800" data-id="element-27">
            Cancelled
          </span>;
      default:
        return null;
    }
  };
  return <div className="bg-white rounded-lg shadow" data-id="element-28">
      {/* Header with actions */}
      <div className="p-6 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0" data-id="element-29">
        <div className="flex items-center space-x-3" data-id="element-30">
          <div className="relative" data-id="element-31">
            <SearchIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" data-id="element-32" />
            <input type="text" placeholder="Search bookings..." className="pl-10 pr-4 py-2 border rounded-md focus:ring-[#006D77] focus:border-[#006D77] w-full sm:w-64" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-33" />
          </div>
          <div className="relative" data-id="element-34">
            <FilterIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" data-id="element-35" />
            <select className="pl-10 pr-4 py-2 border rounded-md focus:ring-[#006D77] focus:border-[#006D77] appearance-none bg-white" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} data-id="element-36">
              <option value="all" data-id="element-37">All Status</option>
              <option value="confirmed" data-id="element-38">Confirmed</option>
              <option value="pending" data-id="element-39">Pending</option>
              <option value="completed" data-id="element-40">Completed</option>
              <option value="cancelled" data-id="element-41">Cancelled</option>
            </select>
          </div>
        </div>
      </div>
      {/* Booking list */}
      <div className="overflow-x-auto" data-id="element-42">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-43">
          <thead className="bg-gray-50" data-id="element-44">
            <tr data-id="element-45">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-46">
                Booking Details
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-47">
                Property
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-48">
                Dates
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-49">
                Guest
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-50">
                Amount
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-51">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-52">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-53">
            {filteredBookings.map(booking => <tr key={booking.id} className="hover:bg-gray-50" data-id="element-54">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-55">
                  <div className="flex flex-col" data-id="element-56">
                    <div className="text-sm font-medium text-gray-900" data-id="element-57">
                      #{booking.id.toString().padStart(5, '0')}
                    </div>
                    <div className="text-xs text-gray-500 flex items-center mt-1" data-id="element-58">
                      <ClockIcon className="h-3 w-3 mr-1" data-id="element-59" />
                      {booking.bookingDate}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-60">
                  <div className="flex items-center" data-id="element-61">
                    <div className="h-10 w-10 rounded-md overflow-hidden flex-shrink-0" data-id="element-62">
                      <img src={booking.propertyImage} alt={booking.propertyTitle} className="h-full w-full object-cover" data-id="element-63" />
                    </div>
                    <div className="ml-4" data-id="element-64">
                      <div className="text-sm font-medium text-gray-900 line-clamp-1" data-id="element-65">
                        {booking.propertyTitle}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-66">
                        {booking.propertyLocation}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-67">
                  <div className="flex flex-col" data-id="element-68">
                    <div className="flex items-center text-sm text-gray-500" data-id="element-69">
                      <span className="font-medium" data-id="element-70">Check-in:</span>
                      <span className="ml-1" data-id="element-71">{booking.checkIn}</span>
                    </div>
                    <div className="flex items-center text-sm text-gray-500 mt-1" data-id="element-72">
                      <span className="font-medium" data-id="element-73">Check-out:</span>
                      <span className="ml-1" data-id="element-74">{booking.checkOut}</span>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-75">
                  <div className="flex items-center" data-id="element-76">
                    <UserIcon className="h-4 w-4 text-gray-400 mr-2" data-id="element-77" />
                    <div className="text-sm text-gray-900" data-id="element-78">
                      {booking.guestName}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-79">
                    {booking.guests} {booking.guests === 1 ? 'guest' : 'guests'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-80">
                  <div className="text-sm font-medium text-gray-900" data-id="element-81">
                    ${booking.totalPrice}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-82">
                  {getStatusBadge(booking.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-83">
                  {booking.status === 'pending' && <>
                      <button className="text-green-500 hover:text-green-700 mr-3" data-id="element-84">
                        <CheckCircleIcon className="h-5 w-5" data-id="element-85" />
                      </button>
                      <button className="text-red-500 hover:text-red-700 mr-3" data-id="element-86">
                        <XCircleIcon className="h-5 w-5" data-id="element-87" />
                      </button>
                    </>}
                  <button className="text-blue-500 hover:text-blue-700" data-id="element-88">
                    <EyeIcon className="h-5 w-5" data-id="element-89" />
                  </button>
                </td>
              </tr>)}
          </tbody>
        </table>
        {filteredBookings.length === 0 && <div className="text-center py-10" data-id="element-90">
            <p className="text-gray-500" data-id="element-91">
              No bookings found matching your criteria
            </p>
          </div>}
      </div>
      {/* Pagination */}
      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between" data-id="element-92">
        <div className="text-sm text-gray-500" data-id="element-93">
          Showing <span className="font-medium" data-id="element-94">{filteredBookings.length}</span>{' '}
          of <span className="font-medium" data-id="element-95">{bookings.length}</span> bookings
        </div>
        <div className="flex items-center space-x-2" data-id="element-96">
          <button className="px-3 py-1 border rounded text-sm disabled:opacity-50" data-id="element-97">
            Previous
          </button>
          <button className="px-3 py-1 bg-[#006D77] text-white rounded text-sm" data-id="element-98">
            1
          </button>
          <button className="px-3 py-1 border rounded text-sm" data-id="element-99">Next</button>
        </div>
      </div>
    </div>;
};
export default AdminBookingList;